import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Heart, Sparkles, X } from "lucide-react";

const PhotographerInvite = ({ isOpen, onClose, onRegister }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{ zIndex: 2147483648 }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none"
            style={{ zIndex: 2147483649 }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-neutral-ivory via-white to-accent-rose/10 rounded-3xl shadow-2xl max-w-md w-full p-8 relative pointer-events-auto border-2 border-accent-gold/20"
            >
              {/* Botón de cerrar */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Iconos decorativos animados */}
              <div className="absolute top-6 left-6">
                <motion.div
                  animate={{
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <Camera className="w-8 h-8 text-primary/60" />
                </motion.div>
              </div>

              <div className="absolute top-6 right-16">
                <motion.div
                  animate={{
                    y: [0, -8, 0],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <Sparkles className="w-6 h-6 text-accent-gold" />
                </motion.div>
              </div>

              {/* Icono principal con animación */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="flex justify-center mb-6 mt-8"
              >
                <div className="relative">
                  {/* Anillo pulsante de fondo */}
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.1, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute inset-0 bg-gradient-to-br from-red-500 to-pink-500 rounded-full blur-xl"
                  />

                  {/* Icono principal */}
                  <div className="relative bg-gradient-to-br from-red-500 to-pink-500 rounded-full p-6 shadow-lg">
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <Camera className="w-12 h-12 text-white" />
                    </motion.div>

                    {/* Corazón flotante */}
                    <motion.div
                      animate={{
                        y: [0, -20, 0],
                        x: [0, 10, 0],
                        rotate: [0, 15, 0],
                        opacity: [1, 0.5, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="absolute -top-2 -right-2"
                    >
                      <Heart className="w-8 h-8 text-red-500 fill-red-500" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              {/* Título */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-script text-primary text-center mb-3"
              >
                ¡No Te Quedes Fuera!
              </motion.h2>

              {/* Mensaje principal */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center text-gray-700 mb-6 font-serif leading-relaxed"
              >
                <span className="block text-lg font-semibold text-primary mb-2">
                  Conviértete en nuestro mejor fotógrafo
                </span>
                <span className="text-base">
                  Agradecemos que le des{" "}
                  <Heart className="inline w-4 h-4 text-red-500 fill-red-500" />{" "}
                  a nuestras fotos, pero{" "}
                  <span className="font-bold text-secondary">
                    no te quedes fuera
                  </span>{" "}
                  de capturar tus mejores tomas y compartirlas con todos.
                </span>
              </motion.p>

              {/* Características/beneficios */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white/60 rounded-2xl p-4 mb-6 border border-accent-gold/20"
              >
                <div className="flex items-start gap-3 mb-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <Sparkles className="w-5 h-5 text-accent-gold flex-shrink-0 mt-1" />
                  </motion.div>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-primary">
                      Captura momentos únicos
                    </span>{" "}
                    y compártelos con la familia
                  </p>
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Camera className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  </motion.div>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-primary">
                      Sube fotos ilimitadas
                    </span>{" "}
                    desde tu dispositivo
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <motion.div
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Heart className="w-5 h-5 text-red-500 fill-red-500 flex-shrink-0 mt-1" />
                  </motion.div>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-primary">
                      Recibe likes y comentarios
                    </span>{" "}
                    de otros invitados
                  </p>
                </div>
              </motion.div>

              {/* Botones de acción */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col gap-3"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onRegister}
                  className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white font-semibold py-4 px-6 rounded-full shadow-lg hover:shadow-pink-500/50 transition-all duration-300 flex items-center justify-center gap-2 group"
                >
                  <Camera className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span>¡Quiero Ser Fotógrafo!</span>
                  <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={onClose}
                  className="w-full bg-white/80 text-gray-600 font-medium py-3 px-6 rounded-full border-2 border-gray-200 hover:border-gray-300 transition-all duration-300"
                >
                  Tal vez más tarde
                </motion.button>
              </motion.div>

              {/* Decoración inferior */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-6 text-center text-xs text-gray-500"
              >
                <span className="flex items-center justify-center gap-1">
                  <Sparkles className="w-3 h-3 text-accent-gold" />
                  Solo te tomará un momento registrarte
                  <Sparkles className="w-3 h-3 text-accent-gold" />
                </span>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PhotographerInvite;
